import type { Icon } from "@phosphor-icons/react";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { cn } from "~/foundation/utils/cn";

export type ButtonVariant = "primary" | "neutral" | "danger" | "ghost";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon, rendered on the inline-start edge (flips automatically in RTL). */
  icon?: Icon;
  fullWidth?: boolean;
  children?: ReactNode;
}

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-sm font-medium whitespace-nowrap " +
  "transition-colors duration-100 ease-in-out motion-reduce:transition-none " +
  "disabled:opacity-50 disabled:pointer-events-none select-none cursor-pointer";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-accent text-accent-text hover:opacity-90",
  neutral: "bg-bg text-text border border-input-border hover:bg-hover-bg",
  danger: "bg-danger text-white hover:opacity-90",
  ghost: "bg-transparent text-text hover:bg-hover-bg",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-small",
  md: "h-8 px-3 text-body",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "neutral",
    size = "md",
    icon: IconComponent,
    fullWidth,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && "w-full", className)}
      {...rest}
    >
      {IconComponent ? <IconComponent size={16} weight="bold" aria-hidden /> : null}
      {children}
    </button>
  );
});
